/**
 * Encode resolve transaction for Gnosis Safe
 */
import { encodeFunctionData } from "viem"
import { breakoutMarketAbi } from "../../abi/generated/breakoutMarket"
import { validateAddress, validateBucket, validateDataUri } from "../../internal/validation"
import type { ResolutionParams, SafeTransaction } from "../../types"

export type EncodeResolveParams = ResolutionParams

/**
 * Encode a resolve transaction for Gnosis Safe
 *
 * @example
 * ```ts
 * import { encodeResolve, ipfsUri } from "@breakout/sdk/actions/encode"
 *
 * const safeTx = encodeResolve({
 *   market: "0x...",
 *   winningBucket: 0,
 *   dataUri: ipfsUri("QmXxx..."), // or "ipfs://QmXxx..."
 * })
 * // { to: "0x...", value: "0", data: "0x..." }
 * ```
 *
 * @throws {ValidationError} If market address, winningBucket, or dataUri is invalid
 */
export function encodeResolve(params: EncodeResolveParams): SafeTransaction {
	const { market, winningBucket, dataUri } = params

	// Validate inputs before encoding
	validateAddress(market, "market")
	validateBucket(winningBucket, "winningBucket")
	validateDataUri(dataUri, "dataUri")

	const data = encodeFunctionData({
		abi: breakoutMarketAbi,
		functionName: "resolve",
		args: [winningBucket, dataUri],
	})

	return {
		to: market,
		value: "0",
		data,
	}
}
